import {Typography} from 'onboarding-components';
import * as React from 'react';
import clsx from 'clsx';
import {RiveState, useRive, useStateMachineInput} from '@rive-app/react-canvas';
import isEmpty from 'lodash/isEmpty';

import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {SvgIconReact} from 'common/components/svg-icon';
import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding/assets';
import {DEFAULT_INDUSTRY_KEY, EVENT_INPUT_MAX, INDUSTRY_EVENTS} from 'common/onboarding/immersive-onboarding/constants';
import {TooltipReact} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {Banner, BannerType} from 'common/onboarding/immersive-onboarding/components/banner';
import {Content, Header} from 'common/onboarding/immersive-onboarding/components';
import {TextInput} from 'common/onboarding/immersive-onboarding/components/text-input';
import {
  AppContext,
  IndustryKey,
  SetupGuideContext,
  Step,
  SubStep,
  UserEventPropertiesMapContext,
} from 'common/onboarding/immersive-onboarding/types';
import {promptIntroModal} from 'common/widgets/intro';
import {track, TrackingEvent, TrackingProperty} from 'common/onboarding/immersive-onboarding/tracking';
import rocketImg from 'common/onboarding/immersive-onboarding/assets/rocket.svg';
import {ApiClient} from 'common/onboarding/immersive-onboarding/api-client';
import {scrollToStep} from 'common/onboarding/immersive-onboarding/components/scroll-manager';

import {Section} from '../section/section';
import {EventChartPreviewCarousel} from './chart-preview';
import {ReadyToConnectV2} from './substeps';
import {EventSubsteps} from './phase-1.1';
import {getDefaultEventList, getDefaultEventPropertiesMap, getMapFromDictionary} from './util';
import {HelpText} from './phase-1.1/help-text';
import {EventSubStepContext} from './common';
import styles from './events.module.styl';

const EventBanner = () => {
  const {currentUser} = React.useContext(AppContext);

  return (
    <Banner type={BannerType.Marketing}>
      <Banner.Content title={`New to event based analytics?`}>
        <Banner.Image src={rocketImg} height={40} width={31} />
      </Banner.Content>
      <Banner.Action
        actionName={`Learn how it works`}
        action={() => {
          promptIntroModal({
            'first-name': (currentUser.get().value?.name || ``).split(` `)[0],
          });
          track(TrackingEvent.EventsLearnMoreClick);
        }}
      />
    </Banner>
  );
};

const EventSelector = ({selected, onIndustryKeyChange, events, updateEvents}) => {
  const dropdownOptions = Object.keys(INDUSTRY_EVENTS).map((el) => ({value: el, label: INDUSTRY_EVENTS[el].name}));

  const deleteEvent = (eventIndex) => {
    const updated = events.filter((_, index) => index !== eventIndex);
    updateEvents(updated);
  };

  const inputValidator = (val) => {
    return val.trim().length > 0;
  };

  return (
    <div className={styles.sectionTile}>
      <SelectReact
        sections={[{items: dropdownOptions}]}
        disableSearch
        onChange={(ev) => {
          onIndustryKeyChange({industryKey: ev.detail.item.value as IndustryKey});
        }}
        theme={SelectTheme.Tertiary}
        alwaysShowCaret
        menuWidth={150}
      >
        <div slot={`trigger`}>
          <Typography weight='regular' variant='bodySm' style={{fontSize: 14}}>
            {INDUSTRY_EVENTS[selected].name} <SvgIconReact icon='caret-down' style={{minHeight: 17, height: 17}} />
          </Typography>
        </div>
      </SelectReact>
      <div className={styles.eventContainer}>
        {events.map((event, eventIndex) => (
          <div key={`${INDUSTRY_EVENTS[selected].name}-${event}`} className={styles.eventEditorRow}>
            <TextInput
              validator={inputValidator}
              hideValidCheckMark
              defaultValue={event}
              onBlur={(value) => {
                const updated = events.map((event, index) => (index === eventIndex ? value : event));
                updateEvents(updated);
              }}
            />
            <div
              className={clsx(styles.deleteIconWrapper, {[styles.disabled]: events.length === 1 && eventIndex === 0})}
              onClick={() => events.length > 1 && deleteEvent(eventIndex)}
            >
              <SvgIconReact className={styles.deleteIcon} icon={`trashcan`} />
              {events.length === 1 && eventIndex === 0 && (
                <TooltipReact placement={Placement.Top} text={`You must choose at least one event`} />
              )}
            </div>
          </div>
        ))}
        {events.length < EVENT_INPUT_MAX && (
          <TextInput
            key={Math.random()} // Hack to trick React rendering...
            validator={inputValidator}
            hideValidCheckMark
            placeholder={`Add Your Own Event`}
            onBlur={(value) => {
              if (value.trim().length > 0) {
                const updated = [...events, value.trim()];
                updateEvents(updated);
              }
            }}
            style={{width: `calc(100% - 38px)`}} // 38px is the width of delete icon + gap from container
          />
        )}
        {/* -1 due to existing input box (5 events + 1 event input) */}
        {events.length >= EVENT_INPUT_MAX - 1 && (
          <Typography variant='captionSm' className={styles.eventLimitText}>
            We suggest tracking 6 or fewer events to start
          </Typography>
        )}
      </div>
    </div>
  );
};

const ReadyToConnectSection = ({selected}) => {
  const {completed} = React.useContext(SetupGuideContext);
  const {setEventSubStep} = React.useContext(EventSubStepContext);

  return (
    <Section
      scrollable
      active={completed[Step.Events]}
      subStepId={SubStep.ReadyToConnect}
      title={`Great work! Insights are one step away.`}
      subTitle={`You are ready to connect your data to Mixpanel`}
      spyCallback={() => {
        setEventSubStep(SubStep.ReadyToConnect);
      }}
    >
      <ReadyToConnectV2 selected={selected} />
    </Section>
  );
};

const ConfirmEventsSection = ({selected, updateSelected}) => {
  const {setEventSubStep} = React.useContext(EventSubStepContext);
  const {setupGuideInfo, setSetupGuideInfo, setStep, completed} = React.useContext(SetupGuideContext);
  const {currentEventPropertiesMap, setCurrentEventPropertiesMap, eventKeys, setEventKeys} =
    React.useContext(UserEventPropertiesMapContext);

  // eslint-disable-next-line sonarjs/no-identical-functions
  const onIndustryKeyChange = ({industryKey}: {industryKey: IndustryKey}) => {
    updateSelected(industryKey);

    let eventsPropertiesMap;
    if (selected !== industryKey && industryKey !== setupGuideInfo.industry) {
      eventsPropertiesMap = getDefaultEventPropertiesMap(industryKey);
    } else if (industryKey === setupGuideInfo.industry) {
      eventsPropertiesMap = getMapFromDictionary(setupGuideInfo.eventsPropertiesMap);
    }

    setCurrentEventPropertiesMap(eventsPropertiesMap);
    setEventKeys(Array.from(eventsPropertiesMap.keys())); // don't set key from effect -  set atomically
  };

  const updateEvents = (eventList: string[]) => {
    const updatedEventPropertiesMap = new Map<string, string[]>(
      eventList.map((event) => [event, currentEventPropertiesMap.get(event) ?? []]), // update the map to carry over existing values
    );
    setCurrentEventPropertiesMap(updatedEventPropertiesMap);
    setEventKeys(eventList); // update key separately to maintain order
  };

  return (
    <Section
      active={completed[Step.Events]}
      className={styles.welcomePadding}
      scrollable
      spyCallback={() => {
        setEventSubStep(SubStep.EventConfirmation);
      }}
      subStepId={SubStep.EventConfirmation}
      sectionLabel='1'
      title={`Choose your first events`}
      subTitle={`Events are meaningful actions users take in your product. The events below are a good starting point for your industry. You may edit this list to fit your needs.`}
      onNext={() => {
        ApiClient.updateSetupGuideInfo(setupGuideInfo.projectId, selected, null, currentEventPropertiesMap).then(
          (result) => {
            setSetupGuideInfo(result);
          },
        );

        track(TrackingEvent.SubStepCompleted, {[TrackingProperty.SubStepName]: `Event confirmation`});
        scrollToStep(SubStep.Segmentation);
      }}
      skipCTA={`Back`}
      onSkip={() => {
        track(TrackingEvent.Back, {[TrackingProperty.SubStepName]: `Event confirmation`});
        setStep(Step.Welcome);
      }}
      headerComponent={<EventBanner />}
    >
      <div className={styles.breakdownContent}>
        <div className={styles.selector}>
          <EventSelector
            selected={selected}
            events={eventKeys}
            onIndustryKeyChange={onIndustryKeyChange}
            updateEvents={updateEvents}
          />
        </div>
        {/* TODO(immersive onboarding) combine previewV1 styling after phase 2 100% rollout */}
        <div className={clsx(styles.preview, styles.previewV2)}>
          <EventChartPreviewCarousel events={eventKeys} />
        </div>
      </div>
    </Section>
  );
};

const BreakdownPreviewSection = () => {
  const [bounceInput, setBounceInput] = React.useState(false);
  const [showProperties, setShowProperties] = React.useState(false);

  // use context to hold the UI state here
  const {setEventSubStep} = React.useContext(EventSubStepContext);
  const {setupGuideInfo, setSetupGuideInfo, completed} = React.useContext(SetupGuideContext);
  const {currentEventPropertiesMap} = React.useContext(UserEventPropertiesMapContext);

  const stateMachineName = `Wave A`;
  const stateMachineInputName = `Wave trigger`;
  const stateMachineArtBoardName = `Wave A Artboard`;

  const {rive, RiveComponent}: RiveState = useRive({
    src: IMMERSIVE_ASSETS.wave,
    stateMachines: [stateMachineName],
    artboard: stateMachineArtBoardName,
    autoplay: true,
  });

  const waveInput = useStateMachineInput(rive, stateMachineName, stateMachineInputName);

  React.useEffect(() => {
    if (completed[Step.Events]) {
      setShowProperties(true);
    }
  }, [completed]);

  return (
    <Section
      active={completed[Step.Events]}
      scrollable
      sectionLabel='2'
      subStepId={SubStep.Segmentation}
      spyCallback={() => {
        setEventSubStep(SubStep.Segmentation);
      }}
      title='Choose the properties you want track with each event'
      subTitle={
        <span>
          Below are popular selections — in some cases Mixpanel will track these by default. {` `}
          <a
            href={`https://docs.mixpanel.com/docs/tracking/how-tos/events-and-properties`}
            target={`_blank`}
            onClick={() => {
              track(TrackingEvent.LearnMoreClick);
            }}
          >{`Learn more.`}</a>
        </span>
      }
      onNext={() => {
        track(TrackingEvent.SubStepCompleted, {[TrackingProperty.SubStepName]: `Property breakdown`});
        scrollToStep(SubStep.ReadyToConnect);
        ApiClient.updateSetupGuideInfo(
          setupGuideInfo.projectId,
          setupGuideInfo.industry,
          null,
          currentEventPropertiesMap,
        ).then((result) => {
          setSetupGuideInfo(result);
        });
      }}
      disabledCallback={() => {
        setBounceInput(true);
        setTimeout(() => {
          setBounceInput(false);
          waveInput?.fire();
        }, 300);
      }}
      disableNext={!showProperties}
      skipCTA={`Back`}
      onSkip={
        showProperties &&
        (() => {
          track(TrackingEvent.Back, {[TrackingProperty.SubStepName]: `Properties Editing`});
          scrollToStep(SubStep.EventConfirmation);
        })
      }
    >
      <EventSubsteps showProperties={showProperties} setShowProperties={setShowProperties} />
      <HelpText showProperties={showProperties} animationTrigger={{bounceInput}}>
        <RiveComponent style={{height: `28px`, marginBottom: `4px`, width: `28px`}} />
      </HelpText>
    </Section>
  );
};

const EventsContent = () => {
  const {setupGuideInfo} = React.useContext(SetupGuideContext);

  const [selected, updateSelected] = React.useState<IndustryKey>(DEFAULT_INDUSTRY_KEY);
  const [userEventPropertiesMap, setUserEventPropertiesMap] = React.useState(new Map());
  const [eventKeys, setEventKeys] = React.useState<string[]>([]);

  const mountRef = React.useRef(false);
  React.useEffect(() => {
    if (!mountRef.current) {
      if (isEmpty(setupGuideInfo.eventsPropertiesMap)) {
        setUserEventPropertiesMap(getDefaultEventPropertiesMap(setupGuideInfo.industry));
        setEventKeys(getDefaultEventList(setupGuideInfo.industry));
      } else {
        setUserEventPropertiesMap(
          new Map<string, string[]>(
            Object.keys(setupGuideInfo.eventsPropertiesMap).map((event) => [
              event,
              setupGuideInfo.eventsPropertiesMap[event],
            ]),
          ),
        );
        setEventKeys(Object.keys(setupGuideInfo.eventsPropertiesMap));
      }

      updateSelected(setupGuideInfo.industry);

      mountRef.current = true;
    }
  }, [setupGuideInfo]);

  return (
    <UserEventPropertiesMapContext.Provider
      value={{
        eventKeys,
        setEventKeys,
        currentEventPropertiesMap: userEventPropertiesMap,
        setCurrentEventPropertiesMap: setUserEventPropertiesMap,
      }}
    >
      <ConfirmEventsSection selected={selected} updateSelected={updateSelected} />
      <BreakdownPreviewSection />
      <ReadyToConnectSection selected={selected} />
    </UserEventPropertiesMapContext.Provider>
  );
};
export const Events = () => {
  const [eventSubStep, setEventSubStep] = React.useState<SubStep>(SubStep.EventConfirmation);
  const {completed} = React.useContext(SetupGuideContext);

  return (
    <EventSubStepContext.Provider value={{eventSubStep, setEventSubStep}}>
      <Header
        title={`Personalize Setup`}
        step={`step 2`}
        imgSrc={IMMERSIVE_ASSETS.toggles}
        duration={3}
        completed={completed[Step.Events]}
      />
      <Content>
        <EventsContent />
      </Content>
    </EventSubStepContext.Provider>
  );
};
